import React, { useMemo, useState } from "react";
import { GripVertical, Plus, Trash2, X, Clock, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2 rounded-xl border outline-none text-sm";
const inputStyle = { borderColor: "rgba(15,18,38,0.12)", background: "rgba(15,18,38,0.02)" };
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// CaryPact carries eight decimals; nobody reads past four.
const ca = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

const Field = ({ label, children }) => (
  <label className="block">
    <span className="kpi-label mb-1 block">{label}</span>
    {children}
  </label>
);

const ParamInput = ({ spec, value, onChange, wallets, catalogue, tid, reads }) => {
  if (spec.type === "wallet") {
    return (
      <select className={inputCls} style={inputStyle} value={value || ""} data-testid={tid}
        onChange={(e) => onChange(e.target.value)}>
        <option value="">Pick a wallet…</option>
        {wallets.map((w) => (
          <option key={w.id} value={w.id}>
            {w.label}
            {reads === "revenue"
              ? ` · ${ca(w.revenue_waiting)} CA revenue waiting${w.cp_seen ? "" : " · not synced yet"}`
              : ` · ${ca(w.available_ca)} CA spare`}
            {w.address ? "" : " · no wallet address on file"}
          </option>
        ))}
      </select>
    );
  }
  if (spec.type === "stake_type") {
    return (
      <select className={inputCls} style={inputStyle} value={value ?? 0} data-testid={tid}
        onChange={(e) => onChange(Number(e.target.value))}>
        {catalogue.stake_types.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    );
  }
  if (spec.type === "lock_days") {
    return (
      <select className={inputCls} style={inputStyle} value={value ?? 360} data-testid={tid}
        onChange={(e) => onChange(Number(e.target.value))}>
        {catalogue.lock_days.map((d) => <option key={d} value={d}>{d} days</option>)}
      </select>
    );
  }
  return (
    <input className={inputCls} style={inputStyle} data-testid={tid}
      type={spec.type === "email" ? "email" : "number"} step="any"
      value={value ?? ""} placeholder={spec.type === "email" ? "you@example.com" : "0"}
      onChange={(e) => onChange(spec.type === "email" ? e.target.value : e.target.value === "" ? "" : Number(e.target.value))} />
  );
};

const StepCard = ({ step, spec, index, wallets, catalogue, onChange, onRemove, onDragStart, onDragOver, onDrop }) => (
  <div className="glass p-3.5" draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
    data-testid={`rpa-step-${index}`}>
    <div className="flex items-start gap-2.5">
      <GripVertical size={15} className="text-ink-3 mt-1.5 cursor-grab shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="chip" style={{ background: spec?.group === "condition" ? "rgba(180,83,9,0.12)" : "rgba(15,185,153,0.14)", color: spec?.group === "condition" ? "#B45309" : "#067a66" }}>
            {spec?.group === "condition" ? "only if" : "then"}
          </span>
          <select className="text-sm font-semibold bg-transparent outline-none flex-1 cursor-pointer"
            value={step.kind} data-testid={`rpa-step-kind-${index}`}
            onChange={(e) => onChange({ kind: e.target.value, params: {} })}>
            {["condition", "action"].map((g) => (
              <optgroup key={g} label={g === "condition" ? "Conditions" : "Actions"}>
                {catalogue.steps.filter((s) => s.group === g).map((s) => (
                  <option key={s.kind} value={s.kind}>{s.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <button type="button" onClick={onRemove} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5"
            data-testid={`rpa-step-remove-${index}`}><Trash2 size={13} className="text-ink-3" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {(spec?.params || []).map((p) => (
            <Field key={p.key} label={p.label || (p.type === "wallet" ? "Wallet" : p.key.replace(/_/g, " "))}>
              <ParamInput spec={p} catalogue={catalogue} wallets={wallets}
                reads={step.kind.includes("revenue") ? "revenue" : "spare"}
                value={step.params?.[p.key] ?? p.default}
                tid={`rpa-step-${index}-${p.key}`}
                onChange={(v) => onChange({ ...step, params: { ...step.params, [p.key]: v } })} />
            </Field>
          ))}
        </div>
        {spec?.plain && (
          <div className="mt-2.5 text-[12px] leading-relaxed" style={{ color: "rgba(15,18,38,0.62)" }}
            data-testid={`rpa-step-plain-${index}`}>
            {spec.plain}
            {spec.where && (
              <span className="block mt-1 text-[11px]" style={{ color: "rgba(15,18,38,0.45)" }}>
                Same number in CaryPact: {spec.where}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const PresetPicker = ({ presets, wallets, onPick, onBlank }) => {
  // Show the wallet that would actually trigger today, so a preset reads real.
  const hot = [...(wallets || [])].sort((a, b) => (b.revenue_waiting || 0) - (a.revenue_waiting || 0))[0];
  return (
    <div className="space-y-3" data-testid="rpa-presets">
      <p className="text-sm text-ink-2">
        Pick one and I'll fill in the steps — you only choose the wallet and the amount.
        {hot?.revenue_waiting > 0 && (
          <> Right now <b>{hot.label}</b> has <b>{ca(hot.revenue_waiting)} CA</b> of mining revenue waiting.</>
        )}
      </p>
      {(presets || []).map((p) => (
        <button key={p.key} type="button" onClick={() => onPick(p)} data-testid={`rpa-preset-${p.key}`}
          className="w-full text-left glass p-4 transition-transform hover:-translate-y-0.5">
          <div className="font-display text-base font-semibold tracking-tight">{p.name}</div>
          <div className="text-[13px] text-ink-2 mt-1">{p.one_liner}</div>
          <div className="text-[11.5px] mt-1.5" style={{ color: "rgba(15,18,38,0.45)" }}>{p.why}</div>
        </button>
      ))}
      <button type="button" onClick={onBlank} className="btn-secondary w-full" data-testid="rpa-preset-blank">
        I'd rather build my own
      </button>
    </div>
  );
};

const Glossary = ({ glossary }) => {
  const [open, setOpen] = useState(false);
  if (!glossary?.length) return null;
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(15,18,38,0.08)" }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="rpa-glossary-toggle"
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-[12.5px] font-semibold"
        style={{ background: "rgba(15,18,38,0.02)" }}>
        <HelpCircle size={13} style={{ color: "#B45309" }} />
        What do these words mean?
        <span className="ml-auto text-ink-3 font-normal">{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <div className="px-3.5 py-3 space-y-2.5" data-testid="rpa-glossary">
          {glossary.map((g) => (
            <div key={g.term} className="text-[12px] leading-relaxed">
              <b>{g.term}</b> — <span style={{ color: "rgba(15,18,38,0.62)" }}>{g.means}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const RecipeBuilder = ({ catalogue, recipe, onClose, onSave }) => {
  const [picked, setPicked] = useState(!!recipe);
  const [name, setName] = useState(recipe?.name || "");
  const [trigger, setTrigger] = useState(recipe?.trigger || { cadence: "daily", at: "07:00", weekday: 1 });
  const [steps, setSteps] = useState(recipe?.steps?.length ? recipe.steps : []);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const specs = useMemo(() => Object.fromEntries(catalogue.steps.map((s) => [s.kind, s])), [catalogue]);

  const usePreset = (p) => {
    setName(p.name);
    setTrigger({ cadence: "daily", at: "09:00", weekday: 1, ...(p.trigger || {}) });
    // Point it at the wallet that has the most waiting, so it works on the first run.
    const best = [...(catalogue.wallets || [])]
      .sort((a, b) => (b.revenue_waiting || 0) - (a.revenue_waiting || 0))
      .find((w) => w.address);
    setSteps((p.steps || []).map((s) => ({
      ...s,
      params: { ...s.params, ...(best && specs[s.kind]?.params?.some((x) => x.type === "wallet")
        ? { wallet_id: best.id } : {}) },
    })));
    setPicked(true);
  };

  const move = (from, to) => {
    if (from === to || to == null) return;
    const next = [...steps];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setSteps(next);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give it a name"); return; }
    setBusy(true);
    try {
      await onSave({ name, enabled: recipe?.enabled ?? false, trigger, steps });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 overflow-y-auto" data-testid="rpa-builder">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.45)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[720px] my-8 rounded-2xl overflow-hidden fade-up"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        <div className="flex items-center gap-3 px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
          <h3 className="font-display text-xl font-semibold tracking-tight">
            {recipe ? "Edit automation" : "New automation"}
          </h3>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5"
            data-testid="rpa-builder-close"><X size={16} /></button>
        </div>

        {!picked ? (
          <div className="px-6 py-5">
            <PresetPicker presets={catalogue.presets} wallets={catalogue.wallets}
              onPick={usePreset}
              onBlank={() => {
                setSteps([{ kind: "condition.revenue_above", params: { amount: 0.02 } },
                          { kind: "action.withdraw_revenue", params: { min_amount: 0.02 } }]);
                setPicked(true);
              }} />
          </div>
        ) : (
        <div className="px-6 py-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name it">
              <input className={inputCls} style={inputStyle} value={name} data-testid="rpa-name"
                placeholder="Morning compound — Stu Staking" onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="How often">
                <select className={inputCls} style={inputStyle} value={trigger.cadence} data-testid="rpa-cadence"
                  onChange={(e) => setTrigger({ ...trigger, cadence: e.target.value })}>
                  {catalogue.cadences.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={trigger.cadence === "hourly" ? "At minute past" : "At (UTC)"}>
                <input className={inputCls} style={inputStyle} value={trigger.at} data-testid="rpa-at"
                  placeholder="07:00" onChange={(e) => setTrigger({ ...trigger, at: e.target.value })} />
              </Field>
            </div>
          </div>
          {trigger.cadence === "weekly" && (
            <Field label="On">
              <select className={inputCls} style={inputStyle} value={trigger.weekday ?? 1} data-testid="rpa-weekday"
                onChange={(e) => setTrigger({ ...trigger, weekday: Number(e.target.value) })}>
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </Field>
          )}

          <div className="space-y-2.5">
            {steps.map((s, i) => (
              <StepCard key={s.id || i} step={s} spec={specs[s.kind]} index={i}
                wallets={catalogue.wallets} catalogue={catalogue}
                onChange={(next) => setSteps(steps.map((x, j) => (j === i ? { ...next, id: s.id } : x)))}
                onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
                onDragStart={() => setDrag(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { move(drag, i); setDrag(null); }} />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" data-testid="rpa-add-condition"
              onClick={() => setSteps([...steps, { kind: "condition.revenue_above", params: { amount: 0.02 } }])}>
              <Plus size={13} /> Add an "only if"
            </button>
            <button type="button" className="btn-secondary" data-testid="rpa-add-action"
              onClick={() => setSteps([...steps, { kind: "action.withdraw_revenue", params: { min_amount: 0.02 } }])}>
              <Plus size={13} /> Add a "then do"
            </button>
            <div className="ml-auto text-[11.5px] text-ink-3 inline-flex items-center gap-1.5">
              <Clock size={12} /> steps run top to bottom; if an "only if" isn't met, nothing below it happens
            </div>
          </div>

          <Glossary glossary={catalogue.glossary} />
        </div>
        )}

        <div className="px-6 py-4 flex items-center gap-2" style={{ borderTop: "1px solid rgba(15,18,38,0.07)", background: "rgba(15,18,38,0.02)" }}>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="rpa-cancel">Cancel</button>
          {picked && !recipe && (
            <button type="button" onClick={() => setPicked(false)} className="btn-secondary" data-testid="rpa-back-to-presets">
              Back
            </button>
          )}
          {picked && <button type="button" onClick={save} disabled={busy} className="btn-primary ml-auto" data-testid="rpa-save">
            {busy ? "Saving…" : recipe ? "Save changes" : "Create automation"}
          </button>}
        </div>
      </div>
    </div>
  );
};
